import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import AppButton from "../../../components/ui/AppButton";
import analyticsService from "../../../services/university-admin/analyticsService";

const UMAnalyticsEventsTable = ({ filters }) => {
  const { t } = useTranslation(['universityManager']);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      const res = await analyticsService.getEvents({ ...filters, page: currentPage, size: pageSize });
      if (res.success) {
        setEvents(res.data.content || res.data.data || []);
        setTotalPages(res.data.totalPages || 0);
        setTotalElements(res.data.totalElements || 0);
      } else {
        setEvents([]);
      }
      setLoading(false);
    };
    fetchEvents();
  }, [filters, currentPage, pageSize]);

  return (
    <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '12px' }}>
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h5 className="card-title fw-bold mb-0">{t("analytics.eventsTable.detailedEventsData")}</h5>
          <select className="form-select form-select-sm border-0 bg-light" style={{ width: 'auto', borderRadius: '8px' }} value={pageSize} onChange={(e) => { setPageSize(parseInt(e.target.value)); setCurrentPage(0); }}>
            <option value={10}>10 {t("analytics.eventsTable.perPage")}</option>
            <option value={20}>20 {t("analytics.eventsTable.perPage")}</option>
            <option value={50}>50 {t("analytics.eventsTable.perPage")}</option>
          </select>
        </div>

        {loading ? (
          <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover align-middle border-bottom">
              <thead className="table-light text-muted">
                <tr>
                  <th className="rounded-start">{t("analytics.eventsTable.eventName")}</th>
                  <th>{t("analytics.eventsTable.organizer")}</th>
                  <th>{t("analytics.eventsTable.date")}</th>
                  <th>{t("analytics.eventsTable.category")}</th>
                  <th>{t("analytics.eventsTable.scope")}</th>
                  <th className="text-end rounded-end">{t("analytics.eventsTable.participants")}</th>
                </tr>
              </thead>
              <tbody>
                {events.length === 0 ? (
                  <tr><td colSpan="6" className="text-center py-4 text-muted">{t("analytics.eventsTable.noEventsFound")}</td></tr>
                ) : (
                  events.map(ev => (
                    <tr key={ev.eventId}>
                      <td className="fw-semibold text-dark">{ev.eventName}</td>
                      <td>{ev.organizerClub}</td>
                      <td>{new Date(ev.date).toLocaleDateString()}</td>
                      <td><span className="badge bg-light text-secondary border">{ev.category}</span></td>
                      <td>{ev.scope}</td>
                      <td className="text-end fw-bold text-primary">{ev.participants}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="d-flex justify-content-between align-items-center mt-3">
            <small className="text-muted fw-semibold">{t("analytics.eventsTable.showing")} {currentPage * pageSize + 1} {t("analytics.eventsTable.to")} {Math.min((currentPage + 1) * pageSize, totalElements)} {t("analytics.eventsTable.of")} {totalElements}</small>
            <div className="d-flex gap-2">
              <AppButton variant="outline" className="btn-sm rounded-pill px-3" onClick={() => setCurrentPage(p => Math.max(0, p - 1))} disabled={currentPage === 0}>{t("analytics.eventsTable.prev")}</AppButton>
              <AppButton variant="outline" className="btn-sm rounded-pill px-3" onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))} disabled={currentPage === totalPages - 1}>{t("analytics.eventsTable.next")}</AppButton>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UMAnalyticsEventsTable;
